import React, { useState } from 'react'
import { FileText, Download, Eye, Calendar, User, Trash2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, ShadcnButton } from '../ui'
import { DocumentViewer } from './DocumentViewer'
import { formatDateToBR } from '../../utils/formatters'

export interface EntityDocument {
 id: string
 name: string
 url: string
 type?: string
 expiryDate?: string
 uploadedBy?: {
 id: string
 name: string
 email: string
 }
 createdAt: string
}

interface DocumentListProps {
 documents: EntityDocument[]
 onRemoveDocument?: (docId: string) => void
 readOnly?: boolean
 title?: string
 emptyMessage?: string
 className?: string
}

const documentTypeLabels: Record<string, string> = {
 address_proof: 'Comprovante de Endereço',
 cnh: 'CNH (Carteira Nacional de Habilitação)',
 payment_receipt: 'Comprovante de Pagamento',
 inspection: 'Vistoria Veicular',
 crlv: 'CRLV (Certificado de Registro)',
 debts: 'Débitos',
 fines: 'Multas',
 other: 'Outros',
}

const documentTypeColors: Record<string, string> = {
 address_proof: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 cnh: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
 payment_receipt: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
 inspection: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
 crlv: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
 debts: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
 fines: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
 other: 'bg-surface text-foreground',
}

export const DocumentList: React.FC<DocumentListProps> = ({
 documents,
 onRemoveDocument,
 readOnly = false,
 title = 'Documentos',
 emptyMessage = 'Nenhum documento cadastrado',
 className = '',
}) => {
 const [selectedDocument, setSelectedDocument] = useState<EntityDocument | null>(null)

 const handleDownloadDocument = (url: string, name: string) => {
 const a = document.createElement('a')
 a.href = url
 a.download = name
 a.target = '_blank'
 a.click()
 }

 if (documents.length === 0) {
 return (
 <Card className={className}>
 <CardHeader>
 <CardTitle className="flex items-center">
 <FileText className="h-5 w-5 mr-2" />
 {title}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-center py-8">
 <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
 <p className="text-muted-foreground">{emptyMessage}</p>
 </div>
 </CardContent>
 </Card>
 )
 }

 return (
 <>
 <Card className={className}>
 <CardHeader>
 <CardTitle className="flex items-center justify-between">
 <div className="flex items-center">
 <FileText className="h-5 w-5 mr-2" />
 {title}
 </div>
 <span className="text-sm font-normal text-muted-foreground">
 {documents.length} {documents.length === 1 ? 'documento' : 'documentos'}
 </span>
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 {documents.map((doc) => {
 return (
 <div
 key={doc.id}
 className="p-4 bg-card border border-border rounded-lg hover:border-accent dark:hover:border-primary-400 transition-colors"
 >
 <div className="flex items-start justify-between gap-4">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-2">
 <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
 <h4 className="text-sm font-semibold text-foreground truncate">
 {doc.name}
 </h4>
 </div>

 {/* Badge de categoria */}
 {doc.type && (
 <div className="mb-2">
 <span
 className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
 documentTypeColors[doc.type] || documentTypeColors.other
 }`}
 >
 {documentTypeLabels[doc.type] || doc.type}
 </span>
 </div>
 )}

 {/* Informações adicionais */}
 <div className="space-y-1 text-xs text-muted-foreground">
 {doc.uploadedBy && (
 <div className="flex items-center gap-1">
 <User className="h-3 w-3" />
 <span>Enviado por: {doc.uploadedBy.name}</span>
 </div>
 )}
 <div className="flex items-center gap-1">
 <Calendar className="h-3 w-3" />
 <span>{formatDateToBR(doc.createdAt)}</span>
 </div>
 {doc.expiryDate && (
 <div className="flex items-center gap-1">
 <Calendar className="h-3 w-3" />
 <span>Validade: {formatDateToBR(doc.expiryDate)}</span>
 </div>
 )}
 </div>
 </div>

 {/* Botões de ação */}
 <div className="flex flex-col gap-2 flex-shrink-0">
 <ShadcnButton
 type="button"
 variant="ghost"
 size="sm"
 onClick={() => setSelectedDocument(doc)}
 className="p-2"
 title="Visualizar documento"
 >
 <Eye className="h-4 w-4" />
 </ShadcnButton>
 <ShadcnButton
 type="button"
 variant="ghost"
 size="sm"
 onClick={() => handleDownloadDocument(doc.url, doc.name)}
 className="p-2"
 title="Baixar documento"
 >
 <Download className="h-4 w-4" />
 </ShadcnButton>
 {!readOnly && onRemoveDocument && (
 <ShadcnButton
 type="button"
 variant="ghost"
 size="sm"
 onClick={() => onRemoveDocument(doc.id)}
 className="p-2 text-red-600 hover:text-red-700 dark:text-red-400"
 title="Remover documento"
 >
 <Trash2 className="h-4 w-4" />
 </ShadcnButton>
 )}
 </div>
 </div>
 </div>
 )
 })}
 </div>
 </CardContent>
 </Card>
 {selectedDocument && (
 <DocumentViewer
 url={selectedDocument.url}
 name={selectedDocument.name}
 isOpen={true}
 onClose={() => setSelectedDocument(null)}
 />
 )}
 </>
 )
 }

